import { FormInput, FormSelector, Modal } from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { v4 as uuid4 } from "uuid"
import { CustomToast } from "~/components/CustomToast"
import { useInvoiceAggregateQuery } from "~/generated/graphql"
import { MONTHS } from "~/utils/customMonths"

const INITIAL_STATE = {
  attachments: [],
  tenure: undefined,
  payment_cycle: undefined,
  start_date: undefined,
  end_date: undefined,
  total_sales: undefined,
}
export const AddSetutradeProjectionDetails = ({ form, isDisabled = false }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const transaction =
    searchParams.get("setutrade") || searchParams.get("projection")

  const isProjection = Boolean(searchParams.get("projection"))
  const [current, setCurrent] = useState(INITIAL_STATE)
  const [submitted, setSubmitted] = useState(false)

  function inputProps(key) {
    return {
      ...form.getInputProps(key),
      disabled: isDisabled,
    }
  }

  useEffect(() => {
    if (transaction) {
      const currBal = (
        isProjection
          ? form.values?.projection_assessments
          : form.values?.trade_assessments
      )?.find(bal => {
        const id = bal?.id || bal?._id
        return id == transaction
      })
      setCurrent(currBal)
    }
  }, [transaction])

  const onClose = () => {
    searchParams.delete("setutrade")
    searchParams.delete("projection")
    setSearchParams(searchParams, { relative: "route" })
    setCurrent(INITIAL_STATE)
    setSubmitted(false)
  }

  const [{ data }] = useInvoiceAggregateQuery({
    variables: {
      where: {
        buyer_id: {
          _eq: form?.values?.user?.shared_id,
        },
        created_at: {
          _gte: current?.start_date
            ? dayjs(current?.start_date).startOf("d")
            : undefined,
          _lte: current?.end_date
            ? dayjs(current?.end_date)?.endOf("d")
            : undefined,
        },
      },
    },
    pause: isProjection,
  })

  useEffect(() => {
    if (!isProjection) {
      setCurrent(val => ({
        ...val,
        total_sales: data?.invoice_aggregate?.aggregate?.sum?.deducted_amount,
      }))
    }
  }, [data])

  return (
    <Modal
      onClose={onClose}
      isOpen={Boolean(transaction)}
      title={isProjection ? "Projection Details" : "Setutrade Details"}
      bodyClassName="max-w-xl overflow-visible"
      primaryActionButtonProps={{
        text: "Yes, Confirm",
        onClick: () => {
          setSubmitted(true)
          if (
            !current?.end_date ||
            !current?.payment_cycle ||
            !current?.start_date ||
            !current?.tenure ||
            !current?.total_sales
          ) {
            return CustomToast({
              title: "Please enter all mandatory values!",
              intent: "danger",
            })
          }

          const currBal = isProjection
            ? form?.values?.projection_assessments || []
            : form?.values?.trade_assessments || []

          if (transaction === "true") {
            //Add New
            const newBalance = [...currBal, { ...current, _id: uuid4() }]
            const updatedState = isProjection
              ? {
                  ...form.values,
                  projection_assessments: newBalance,
                }
              : { ...form.values, trade_assessments: newBalance }
            form.setValues(updatedState)
          } else {
            //Update Existing
            const newBalance = currBal.map(entry => {
              const id = entry?.id || entry?._id
              return id == transaction ? { ...entry, ...current } : entry
            })
            const updatedState = isProjection
              ? {
                  ...form.values,
                  projection_assessments: newBalance,
                }
              : { ...form.values, trade_assessments: newBalance }
            form.setValues(updatedState)
          }
          onClose()
        },
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <section className="grid grid-cols-2 gap-3">
        <FormSelector
          label="Custom Tenure"
          required
          value={current?.tenure}
          onChange={e => {
            const date = new Date()
            setCurrent(val => ({
              ...val,
              tenure: e,
              start_date: e ? (date as any) : undefined,
              end_date: e
                ? (dayjs(date).add((e as any)?.days, "day") as any)
                : undefined,
            }))
          }}
          options={Object.values(MONTHS)}
          helperText={((current?.tenure as any)?.days || "--") + " days"}
          error={submitted && !current?.tenure ? "Please Select Tenure" : ""}
          disabled={isDisabled}
        />
        <FormInput
          label="Payment Cycle"
          required
          value={current?.payment_cycle}
          onChange={e => {
            setCurrent(val => ({ ...val, payment_cycle: e }))
          }}
          rightElement={<p className="mx-2 text-brown-300 text-sm">Days</p>}
          error={
            submitted && !current?.payment_cycle
              ? "Please Enter Payment Cycle"
              : ""
          }
          disabled={isDisabled}
        />

        <FormDateInput
          label="Start Date"
          required
          value={current?.start_date}
          excludeDate={date => {
            return date <= (dayjs(current?.start_date).toDate() as any)
          }}
          onChange={date => {
            setCurrent(val => ({
              ...val,
              start_date: date as any,
            }))
            if (date) {
              setCurrent(val => ({
                ...val,
                end_date: dayjs(date).add(
                  (current?.tenure as any)?.days,
                  "day"
                ) as any,
              }))
            }
          }}
          error={
            submitted && !current?.start_date ? "Please Enter Start Date" : ""
          }
          disabled={isDisabled}
        />
        <FormDateInput
          label="End Date"
          required
          value={current?.end_date}
          excludeDate={date => {
            return (
              date >=
                (dayjs(current?.start_date)
                  .add((current?.tenure as any)?.days, "day")
                  .toDate() as any) ||
              date <= (dayjs(current?.start_date).toDate() as any)
            )
          }}
          onChange={date => {
            setCurrent(val => ({
              ...val,
              end_date: date as any,
            }))
            if (!current?.start_date && date) {
              setCurrent(val => ({
                ...val,
                start_date: dayjs(date).subtract(
                  (current?.tenure as any)?.days,
                  "day"
                ) as any,
              }))
            }
          }}
          error={submitted && !current?.end_date ? "Please Enter End Date" : ""}
          disabled={isDisabled}
        />
        <FormInput
          label="Total Sales"
          required
          className={"col-span-2"}
          value={current?.total_sales}
          onChange={e => {
            setCurrent(val => ({ ...val, total_sales: e }))
          }}
          disabled={!isProjection || isDisabled}
          error={
            submitted && !current?.total_sales ? "Please Enter Total Sales" : ""
          }
        />
      </section>
    </Modal>
  )
}
